import { BehaviorSubject, Observable } from 'rxjs';

import { KalTableDatasource } from './kal-table-datasource';
import { CollectionViewer } from '@angular/cdk/collections';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/classes/kal-table-header';
import { KalTableSortEvent } from 'src/app/_shared/modules/kal-table/classes/managers/kal-table-sort-manager';
import { ColumnsCSV, exportListAsCSV } from 'src/app/utils/kal-table-csv-export.utils';
import { arrayEmpty } from 'src/app/utils/array.utils';
import { NumberRangeType } from 'src/app/_shared/modules/form-component/number-range/number-range.component';
import { DateRange } from 'src/app/_shared/modules/form-component/date-range/date-range.component';
import { KalDate } from '@kalidea/kaligraphi';
import { isBetween } from 'src/app/utils/date.utils';
import { environment } from 'src/environments/environment';
import {
    ColumnFilter, ColumnFilterType,
    ColumnFilterValueType
} from 'src/app/_shared/modules/kal-table/kal-table-header/kal-table-header.component';

export enum KalDataSourceLoadingMode {
    Append = 'append',
    Replace = 'replace'
}

export class KalTableGenericDatasource<T> extends KalTableDatasource<T> {
    protected datasourceRows = new BehaviorSubject<T[]>([]);
    protected unfilteredDatasourceRows = new BehaviorSubject<T[]>([]);
    columnFilters: ColumnFilter[] = [];

    private static normalizeForSearch(value: string): string {
        return value.trim().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
    }

    get rows(): T[] {
        return this.datasourceRows.value;
    }

    get unfilteredRows(): T[] {
        return this.unfilteredDatasourceRows.value;
    }

    /**
     * récupère les données en temps réel
     */
    get rows$(): Observable<T[]> {
        return this.datasourceRows.asObservable();
    }

    get unfilteredRows$(): Observable<T[]> {
        return this.unfilteredDatasourceRows.asObservable();
    }

    get isFiltered(): boolean {
        return this.columnFilters.some(f => !!f.filterValue || !!f.showsOnlyEmptyValues);
    }

    isClosed(): boolean {
        return this.datasourceRows.closed;
    }

    exportAsCSV(exportedColumns: KalTableHeader[], fileName = 'export'): void {
        exportListAsCSV(this.rows, exportedColumns as ColumnsCSV<T>[], fileName);
    }

    setRows(rows: T[], mode = KalDataSourceLoadingMode.Replace): void {
        if (mode === KalDataSourceLoadingMode.Append) {
            rows = this.rows.concat(rows);
        }
        this.unfilteredDatasourceRows.next(rows);
        this.datasourceRows.next(rows);
        this.applyColumnFilters();
    }

    deleteColumnFilter(columnName: string): void {
        let filter = this.columnFilters.find(filter => filter.columnName === columnName);
        if(!!filter) {
            filter.filterValue = null;

            if(filter.showsOnlyEmptyValues) {
                filter.showsOnlyEmptyValues = false;
            }
        }
        this.applyColumnFilters();
    }

    setColumnFilter(columnFilter: ColumnFilter): void {
        const filter = this.columnFilters.find(filter => filter.columnName === columnFilter.columnName);
        if (!!filter) {
            filter.filterValue = columnFilter.filterValue;
            filter.showsOnlyEmptyValues = columnFilter.showsOnlyEmptyValues;
        } else {
            this.columnFilters.push(columnFilter);
        }
        this.applyColumnFilters();
    }

    applyColumnFilters(): void {
        let rows = this.unfilteredDatasourceRows.value;
        if (arrayEmpty(this.columnFilters)) {
            this.datasourceRows.next(rows);
            return;
        }


        rows = rows.filter(row => {

            // Contrôle inversé :
            // si isFiltered = false, cela signifie que la ligne sera affichée sur le kal-table
            let isFiltered = false;
            for (const filter of this.columnFilters.filter(f => !!f.filterValue || !!f.showsOnlyEmptyValues)) {

                // Si la valeur brute est un objet (non-null) et qu'un valueAccessor est fourni
                // (typiquement column.exporter), on projette la cellule en string. Permet le filtre
                // texte sur des colonnes dont la valeur est un objet imbriqué (ex: {id, label}).
                const rawValue = row[filter.columnName];
                const isNonStringObject = rawValue !== null && typeof rawValue === 'object';
                const rowValue = (filter.type === ColumnFilterType.Text && isNonStringObject && filter.valueAccessor)
                    ? filter.valueAccessor(row, filter.columnName)
                    : rawValue as string;
                let filterValue: ColumnFilterValueType = null;

                if (filter.showsOnlyEmptyValues) {
                    isFiltered = !!rowValue;
                } else {
                    switch (filter.type) {
                        case ColumnFilterType.Text:
                            filterValue = filter.filterValue as string;
                            if (!rowValue) {
                                isFiltered = true;
                            } else {
                                const haystack = Array.isArray(rowValue?.['string']) ? rowValue['string'].join('') : rowValue;
                                const normalizedHaystack = KalTableGenericDatasource.normalizeForSearch(haystack);
                                const normalizedNeedle = KalTableGenericDatasource.normalizeForSearch(filterValue);
                                if (!normalizedHaystack.includes(normalizedNeedle)) {
                                    isFiltered = true;
                                }
                            }
                            break;
                        case ColumnFilterType.Number:
                            const {startNumber, endNumber} = filter.filterValue as NumberRangeType;
                            const hasStartNumber = startNumber !== null && startNumber !== undefined;
                            const hasEndNumber = endNumber !== null && endNumber !== undefined;
                            if(hasStartNumber && hasEndNumber) {
                                isFiltered = +rowValue < startNumber || +rowValue > endNumber;
                            } else if(hasStartNumber) {
                                isFiltered = +rowValue < startNumber;
                            } else if(hasEndNumber) {
                                isFiltered = +rowValue > endNumber;
                            }
                            break;
                        case ColumnFilterType.List:
                            filterValue = filter.filterValue as string[];
                            isFiltered = !filterValue.includes(rowValue);
                            break;
                        case ColumnFilterType.Date: {
                            const {startDate, endDate} = filter.filterValue as DateRange;
                            if (!rowValue) {
                                isFiltered = true;
                            } else {
                                const rowDate = new KalDate(rowValue, environment.date.format.isoQuery);
                                isFiltered = !rowDate.valid || !isBetween(rowDate, startDate, endDate);
                            }
                            break;
                        }
                    }
                }

                if(isFiltered) {
                    break;
                }

            }
            return !isFiltered;

        });

        this.datasourceRows.next(rows);
    }

    connect(collectionViewer: CollectionViewer): Observable<T[] | ReadonlyArray<T>> {
        this.datasourceRows.next([]);
        return this.datasourceRows.asObservable();
    }

    disconnect(collectionViewer?: CollectionViewer): void {
        super.disconnect(collectionViewer);
        this.datasourceRows.complete();
    }

    protected getComparator(sort: KalTableSortEvent): (a: T, b: T) => number {
        // si il n'y a pas de tri, ou alors que le tri a déjà été effectué dans l'appel graphql, on renvoi 0
        if (!(sort?.code)) {
            return null;
        }
        // si il y a une fonction de tri spécifique ont retourne celle ci.
        if (!!sort?.customComparator) {
            return sort.customComparator(sort);
        }

        // Si la valeur brute est un objet et qu'un valueAccessor est fourni (typiquement
        // column.exporter), on projette la cellule en string/number triable. Permet le tri
        // sur des colonnes dont la valeur est un objet imbriqué (ex: {id, label}).
        const project = (row: T) => {
            const raw = row[sort.code];
            const isObject = raw !== null && typeof raw === 'object';
            return (isObject && sort.valueAccessor) ? sort.valueAccessor(row, sort.code) : raw;
        };

        return (a, b) => {
            const dir = sort.direction === 'asc' ? 1 : -1;
            const accA = project(a);
            const accB = project(b);
            const type = typeof accA;
            // Formate la valeur en fonction du type
            const valueByType = (value: any): T => {
                if (type === 'string') {
                    return (value || '').toLocaleLowerCase();
                }
                return (value || '');
            };

            return valueByType(accA) > valueByType(accB) ? dir * 1 : dir * -1;
        };
    }
}
